import { ApolloClient } from '@apollo/client';
import { loggerService } from '@brainpool/logger';
import { ConnectionStatus } from '../types/WebSocket';

// Apollo client instance - set by initializeWebSocketHelpers
let apolloClient: ApolloClient;
const docsProcessedIds = new Set<string>();

export function initializeWebSocketHelpers(client: ApolloClient) {
  apolloClient = client;
  loggerService.log({
    message: 'WebSocket helpers initialized',
    logLevel: 'info',
    context: { action: 'initializeWebSocketHelpers' },
  });
}
// Update connection status for a case
export function updateCaseConnectionStatus(caseId: string, status: ConnectionStatus) {
  // This can be used to track WebSocket connection status per case
  loggerService.log({
    message: `Connection status updated for case ${caseId}: ${status}`,
    logLevel: 'info',
    context: { action: 'updateCaseConnectionStatus', caseId, status },
  });
}
export function handleDocumentUpdated(caseId: string, data: any): void {
  if (data?.embeddings && !docsProcessedIds.has(caseId)) {
    docsProcessedIds.add(caseId);
    loggerService.log({
      message: `Documents processing`,
      logLevel: 'success',
      context: {
        action: 'handleDocumentUpdated',
        data: `message data, ${JSON.stringify(data)}, `,
      },
      userMessage: {
        title: `DOCUMENTS PROCESSING`,
        message: `Your data is being embedded and getting ready for AI to analyse.`,
      },
    });
  }
}

export function handleCaseUpdated(caseId: string, data: any): void {
  const caseName = sessionStorage.getItem(`caseName:${caseId}`);
  handleStateUpdate(caseId, 'case_updated', data);

  if (
    data?.status === 'document_processing_completed_screener' ||
    data?.status === 'document_processing_completed_duediligence' ||
    data?.status === 'report_completed'
  ) {
    loggerService.log({
      message: `Document processing completed for case ${caseId}`,
      logLevel: 'success',
      context: { action: 'handleCaseUpdated', caseId, data },
      userMessage: {
        title: `DOCUMENT PROCESSING COMPLETED`,
        message: `Your data in project ${caseName} is ready for due diligence.`,
      },
    });
  }
  if (
    data?.status === 'screener_completed' ||
    data?.status === 'duediligence_completed' ||
    data?.status === 'report_completed'
  ) {
    const message =
      data?.status === 'screener_completed'
        ? 'Case Completed Screening'
        : 'Case Completed Full Due Diligence';
    loggerService.log({
      message: `Case completed ${message}`,
      logLevel: 'success',
      context: { action: 'handleCaseUpdated', caseId, data },
      userMessage: {
        title: `ANALYSIS COMPLETED`,
        message: `Your due diligence process for project ${caseName} is completed and ready for review.`,
      },
    });
    docsProcessedIds.delete(caseId);
  }
}
export function handleStateUpdate(caseId: string, type: string, data: any): void {
  try {
    if (!apolloClient) {
      loggerService.log({
        message: 'Apollo client not initialized',
        logLevel: 'error',
        context: { action: 'handleStateUpdate', caseId },
      });
      return;
    }

    const cacheSnapshot = apolloClient.cache.extract() as Record<string, any>;
    const caseKeys = Object.keys(cacheSnapshot).filter(key => key.includes(caseId));
    const caseIdentifier = caseKeys.length > 0 ? caseKeys[0] : `Case:${caseId}`;

    apolloClient.cache.modify({
      id: caseIdentifier,
      fields: {
        workflow_state(existing = {}) {
          const updated = {
            __typename: 'WorkflowState',
            ...existing,
          };

          if (data.status !== undefined) {
            updated.status = data.status;
          }
          if (data.updated_at !== undefined) {
            updated.updated_at = data.updated_at;
          }
          if (data.data_availability !== undefined) {
            updated.data_availability = {
              __typename: 'DataAvailability',
              ...(existing.data_availability || {}),
              ...data.data_availability,
            };
          }
          if (data.risks !== undefined) {
            updated.risks = data.risks;
          }

          return updated;
        },

        report_path(existing) {
          return data.report_path !== undefined ? data.report_path : existing;
        },
        description(existing) {
          return data.description !== undefined ? data.description : existing;
        },
        details(existing) {
          return data.details !== undefined ? data.details : existing;
        },
        widgets(existing) {
          return data.widgets !== undefined ? data.widgets : existing;
        },
        enrichments(existing = {}) {
          // Check if any enrichment fields exist in data
          const hasEnrichmentFields =
            data.investment_stage !== undefined ||
            data.sector !== undefined ||
            data.fit_score !== undefined ||
            data.usp !== undefined ||
            data.key_technologies !== undefined ||
            data.patents !== undefined ||
            data.founders !== undefined;

          if (!hasEnrichmentFields) return existing;

          loggerService.log({
            message: 'Updating enrichments in cache with data',
            logLevel: 'info',
            context: { data },
          });

          const updated = {
            __typename: 'Enrichments',
            ...existing,
          };

          if (data.investment_stage !== undefined) {
            updated.investment_stage = data.investment_stage;
          }
          if (data.sector !== undefined) {
            updated.sector = data.sector;
          }
          if (data.fit_score !== undefined) {
            updated.fit_score = {
              __typename: 'FitScore',
              ...(existing?.fit_score || {}),
              ...data.fit_score,
            };
          }
          if (data.usp !== undefined) {
            updated.usp = data.usp;
          }
          if (data.key_technologies !== undefined) {
            updated.key_technologies = data.key_technologies;
          }
          if (data.patents !== undefined) {
            updated.patents = data.patents;
          }
          if (data.founders !== undefined) {
            updated.founders = data.founders.map((founder: any) => ({
              __typename: 'FounderInfo',
              ...founder,
            }));
          }

          return updated;
        },
      },
      broadcast: true,
    });

    loggerService.log({
      message: `Cache updated for case ${caseId}`,
      logLevel: 'info',
      context: { action: type, caseId },
    });
  } catch (error) {
    loggerService.log({
      message: `Error updating ${type} for case ${caseId}`,
      logLevel: 'error',
      error: error as Error,
      context: { action: type, caseId },
    });
  }
}
